import { inject, injectable } from 'inversify'
import { Raycaster, Vector3 } from 'three'
import { getPixelSizeInWorld } from '@/plugins/cad/module/common/utils'
import {
  getActiveViewRect,
  getActiveViewSize,
  screenToActiveViewNDC,
} from '@/plugins/cad/module/common/view'
import { TYPES } from '@/plugins/cad/module/container/types'
import type { KeyEventParams, MouseEventParams } from '@/plugins/cad/module/core/common/event'
import { EventName } from '@/plugins/cad/module/core/common/event'
import { Controller } from '@/plugins/cad/module/core/controller/Controller'
import type { IPoint } from '@/plugins/cad/module/utils'
import type { EngineContext } from '@/runtime/EngineContext'
import { DEFAULT_UNPROJECT_Z, KeyCodes } from '@/shared/constants'
import type { MouseTipHelper } from './auxiliaryTool/MouseTipHelper'
import type { CADDefaultTool } from './CADDefaultTool'
import type { CADViewManager } from './manager/CADViewManager'
import type { IPlane } from './manager/DataManager'
import type { HistoryManager } from './manager/HistoryManager'
import type { EntitySelector } from './selector/entitySelector'

/** 单击移动阈值（像素），按下到抬起移动小于此值视为点击 */
const CLICK_MOVE_THRESHOLD_PX = 5

/**
 * CAD 模块的控制器，桥接引擎输入与 CAD 内部工具链，负责事件分发、坐标转换与拾取。
 */
@injectable()
export class CADController extends Controller {
  @inject(TYPES.CADViewManager)
  private readonly viewManager!: CADViewManager

  @inject(TYPES.EntitySelector)
  private readonly entitySelector!: EntitySelector

  @inject(TYPES.MouseTipHelper)
  private readonly mouseTipHelper!: MouseTipHelper

  @inject(TYPES.HistoryManager)
  private readonly historyManager!: HistoryManager

  /** 默认工具，未激活其他工具时接管交互 */
  @inject(TYPES.CADDefaultTool)
  declare defaultTool: CADDefaultTool

  @inject(TYPES.EngineContext)
  private ctx!: EngineContext

  private raycaster: Raycaster = new Raycaster()

  /** 单击检测：记录 pointerDown 按键与位置 */
  private pointerDownInfo: {
    button: number
    screenPos: { x: number; y: number }
  } | null = null

  /** 获取活跃相机 */
  get camera() {
    return this.ctx.getService('ViewportCamera').getActiveCamera()
  }

  /** 获取渲染器（legacy；WebGPU 下读取会抛后端不匹配，仅 WebGL2 路径使用） */
  get renderer() {
    return this.ctx.renderer
  }

  /**
   * 指针宿主 DOM 元素，用于挂载辅助 UI 与绑定事件。
   *
   * 优先取 GraphicsBackend 的 surface canvas；缺少 graphics 的旧上下文回退到 legacy renderer
   * （仅 WebGL2 会走到）。
   */
  get domElement(): HTMLCanvasElement {
    const canvas = this.ctx.graphics?.canvas
    if (canvas) {
      return canvas
    }
    return this.ctx.renderer.domElement
  }

  /** CAD 场景根节点，所有 CAD 对象挂载其下 */
  get scene() {
    return this.ctx.sceneGraph.cadRoot
  }

  protected initialize() {
    // 订阅引擎级事件（相机变化、帧更新）
    this.bindEngineEvents()

    // 框选初始化
    this.entitySelector.frameSelect.mount(this.domElement.parentElement!)
    ;(this.entitySelector.frameSelect as any).setContext?.(
      this.camera,
      this.domElement,
      (point: Vector3) => this.getScreenPosition(point, false)
    )

    // 辅助工具初始化
    this.mouseTipHelper.mount(this.domElement.parentElement!)

    // 将临时 Group 添加到场景中
    this.viewManager.mountTempGroup(this.scene)

    // 撤销/重做后清理选中实体中已删除的项
    this.historyManager.on('undoRedo', this.syncSelectionAfterUndoRedo)
  }

  protected terminate() {
    this.unbindEngineEvents()

    // 探测器、选择器终止
    this.entitySelector.clearSelection()
    this.entitySelector.frameSelect.unmount()

    // 辅助工具终止
    this.mouseTipHelper.dispose()
    this.mouseTipHelper.unmount()

    // 取消撤销/重做监听
    this.historyManager.off('undoRedo', this.syncSelectionAfterUndoRedo)

    // 从场景中移除临时 Group
    this.viewManager.unmountTempGroup()
    this.viewManager.clearTempObjects()
  }

  /** 撤销/重做后同步选中状态：清理已不存在的实体 */
  private syncSelectionAfterUndoRedo = () => {
    const selected = this.entitySelector.getSelected()
    const toRemove = selected.filter((e) => !this.viewManager.getEntity(e.id))
    if (toRemove.length > 0) {
      this.entitySelector.deselect(toRemove)
    }
  }

  // ─── 引擎事件（camera.changed / frame.begin）──────────────

  private bindEngineEvents() {
    this.ctx.events.on('camera.changed', this.onControlsChange)
    this.ctx.events.on('frame.begin', this.onFrameUpdate)
  }

  private unbindEngineEvents() {
    this.ctx.events.off('camera.changed', this.onControlsChange)
    this.ctx.events.off('frame.begin', this.onFrameUpdate)
  }

  private onControlsChange = () => {
    this.eventDispatch(EventName.onControlsChange, {})
  }

  private onFrameUpdate = () => {
    this.eventDispatch(EventName.onViewerUpdate, {})
  }

  // ─── 外部事件入口（由 CadPlugin 通过 InputRouter 驱动）────

  /**
   * 将外部事件分发到 CAD 内部工具链。
   * CadPlugin 的 InputConsumer 负责将 NormalizedPointerEvent 转换为
   * MouseEventParams / KeyEventParams 后调用此方法。
   *
   * @param type - 事件名，见 EventName
   * @param args - 透传给目标处理器的事件参数
   */
  dispatchExternalEvent(type: EventName, ...args: unknown[]) {
    this.eventDispatch(type, ...args)
  }

  /** 记录 pointerDown 按键与位置，用于后续 pointerUp 判定是否为点击 */
  handlePointerDown(event: MouseEvent, screenPos: { x: number; y: number }) {
    this.pointerDownInfo = {
      button: event.button,
      screenPos,
    }
  }

  /**
   * 处理 pointerUp：若按下到抬起移动距离小于阈值则视为点击，分发 onMouseDown 事件。
   *
   * @param event - 原始鼠标事件
   * @param screenPos - 指针屏幕坐标
   * @returns 是否判定为单击并被消费
   */
  handlePointerUp(event: MouseEvent, screenPos: { x: number; y: number }): boolean {
    const pointerDownInfo = this.pointerDownInfo
    this.pointerDownInfo = null
    if (!pointerDownInfo || pointerDownInfo.button !== event.button) {
      return false
    }

    const dx = screenPos.x - pointerDownInfo.screenPos.x
    const dy = screenPos.y - pointerDownInfo.screenPos.y
    if (Math.hypot(dx, dy) > CLICK_MOVE_THRESHOLD_PX) {
      return false
    }

    // 判定为点击，沿用 CAD 工具链既有 onMouseDown 语义
    this.eventDispatch(EventName.onMouseDown, this.buildMouseEventParams(event, screenPos))
    return true
  }

  /**
   * 处理 pointerMove：分发 onMouseMove 并更新鼠标提示位置。
   *
   * @param event - 原始鼠标事件
   * @param screenPos - 指针屏幕坐标
   */
  handlePointerMove(event: MouseEvent, screenPos: { x: number; y: number }) {
    const params = this.buildMouseEventParams(event, screenPos)
    this.eventDispatch(EventName.onMouseMove, params)
    this.mouseTipHelper.updateTransform({ position: params.screenPos })
  }

  /**
   * 处理 keyDown：分发事件 + 全局快捷键（Ctrl+Z / Ctrl+Y）。
   *
   * @param event - 原始键盘事件
   */
  handleKeyDown(event: KeyboardEvent) {
    const params: KeyEventParams = { event }
    this.eventDispatch(EventName.onKeyDown, params)
    // 全局快捷键处理
    if (event.ctrlKey && event.keyCode === KeyCodes.Z) {
      this.historyManager.undo()
    } else if (event.ctrlKey && event.keyCode === KeyCodes.Y) {
      this.historyManager.redo()
    }
  }

  /**
   * 处理 dblclick。
   *
   * @param event - 原始鼠标事件
   * @param screenPos - 指针屏幕坐标
   */
  handleDoubleClick(event: MouseEvent, screenPos: { x: number; y: number }) {
    this.eventDispatch(EventName.onDoubleClick, this.buildMouseEventParams(event, screenPos))
  }

  /**
   * 处理 keyUp。
   *
   * @param event - 原始键盘事件
   */
  handleKeyUp(event: KeyboardEvent) {
    const params: KeyEventParams = { event }
    this.eventDispatch(EventName.onKeyUp, params)
  }

  // ─── 坐标转换 ─────────────────────────────────────────────

  /**
   * 根据屏幕坐标构建 MouseEventParams（含工作平面世界坐标）。
   *
   * @param event - 原始鼠标事件
   * @param screenPos - 指针屏幕坐标
   * @returns 包含屏幕坐标与工作平面坐标的鼠标事件参数
   */
  buildMouseEventParams(event: MouseEvent, screenPos: { x: number; y: number }): MouseEventParams {
    return {
      event,
      screenPos,
      worldPos: this.screenToWorkPlane(screenPos),
    }
  }

  private getActiveViewRect() {
    return getActiveViewRect(this.ctx)
  }

  private getActiveViewSize() {
    return getActiveViewSize(this.ctx)
  }

  /** 屏幕坐标 → 工作平面二维坐标 */
  private screenToWorkPlane(screenPos: { x: number; y: number }): {
    x: number
    y: number
    z: number
  } {
    const camera = this.camera
    const mousePoint = screenToActiveViewNDC(this.ctx, screenPos)
    const intersectPoint = new Vector3()
    this.raycaster.setFromCamera(mousePoint, camera)
    // 获取射线与工作平面的交点
    if (this.raycaster.ray.intersectPlane(this.viewManager.planeHelper, intersectPoint)) {
      // 将三维世界坐标转换为 Group 内部的局部坐标
      const localPoint = this.viewManager.getObjectGroup().worldToLocal(intersectPoint)
      return { x: localPoint.x, y: localPoint.y, z: 0 }
    }
    const fallback = new Vector3(mousePoint.x, mousePoint.y, DEFAULT_UNPROJECT_Z).unproject(camera)
    return { x: fallback.x, y: fallback.y, z: fallback.z }
  }

  /**
   * 获取屏幕上指定像素半径对应的世界空间长度。
   *
   * @param radius - 像素半径
   * @returns 对应的世界空间长度
   */
  getPixelSizeInWorld(radius: number): number {
    return getPixelSizeInWorld(this.camera, this.getActiveViewSize(), radius)
  }

  /**
   * 将本地坐标转换为屏幕坐标。
   *
   * @param localPos - 点位置，默认视为对象组的本地坐标
   * @param isLocalPos - 是否按本地坐标解析，否则按世界坐标
   * @returns 屏幕像素坐标
   */
  getScreenPosition(localPos: IPoint, isLocalPos: boolean = true): { x: number; y: number } {
    const group = this.viewManager.objectManager.getSceneGroup()
    const worldPos = isLocalPos
      ? group.localToWorld(new Vector3(localPos.x, localPos.y, localPos.z || 0))
      : new Vector3(localPos.x, localPos.y, localPos.z || 0)
    const rect = this.getActiveViewRect()
    const projected = worldPos.clone().project(this.camera)
    return {
      x: rect.x + ((projected.x + 1) / 2) * rect.width,
      y: rect.y + ((-projected.y + 1) / 2) * rect.height,
    }
  }

  /**
   * 射线拾取 CAD 对象。
   *
   * @param screenPos - 指针屏幕坐标
   * @returns 命中对象列表，按距离升序排列
   */
  getCADIntersections(screenPos: { x: number; y: number }) {
    const camera = this.camera
    const mousePoint = screenToActiveViewNDC(this.ctx, screenPos)

    this.raycaster.setFromCamera(mousePoint, camera)
    const threshold = this.getPixelSizeInWorld(10)
    const line2Threshold = 5
    this.raycaster.params.Line!.threshold = threshold
    if (!this.raycaster.params.Line2) {
      this.raycaster.params.Line2 = { threshold: line2Threshold }
    } else {
      this.raycaster.params.Line2.threshold = line2Threshold
    }

    const intersectableObjects = this.getCADIntersectableObjects()
    return this.raycaster
      .intersectObjects(intersectableObjects, false)
      .sort((a, b) => a.distance - b.distance)
  }

  /** 获取可拾取的 CAD 对象 */
  private getCADIntersectableObjects() {
    const group = this.viewManager.objectManager.getSceneGroup()
    const objects: import('three').Object3D[] = []
    group.traverse((child) => {
      if (
        (child.visible && (child as any).isMesh) ||
        (child as any).isLine ||
        (child as any).isLine2 ||
        (child as any).isLineSegments2
      ) {
        objects.push(child)
      }
    })
    return objects
  }

  /**
   * 根据平面参数更新临时 Group 的变换矩阵。
   *
   * @param plane - 目标工作平面
   */
  updateGroupByPlane(plane: IPlane) {
    this.viewManager.updateTempGroupByPlane(plane)
  }

  /**
   * 更新平面，并联动更新相关内容。
   *
   * @param plane - 目标工作平面
   */
  updatePlane(plane: IPlane) {
    this.viewManager.dataManager.updatePlane(plane)
    this.viewManager.objectManager.updateGroupByPlane(plane)
    this.viewManager.syncWorkingTransformFromObjectGroup()
  }
}
